/**
 * Cara role-resolution — single source of truth for mapping the
 * platform's user role strings into the Cara role tabs.
 *
 * Feb 2026 refit — the canonical Cara tabs are:
 *   planner · provider · clinician · family · admin
 *
 * The legacy `agency` and `care_provider_agency` / `home_health_agency`
 * / `staffing_agency` roles now resolve to the `provider` tab (the
 * receiving-provider surface). `payer` is intentionally missing from
 * the visible tab set — the payer workspace is parked; those users
 * hit a static "workspace not available yet" reply on the backend
 * (`PARKED_PAYER_REPLY`) and are not shown a Cara tab.
 *
 * MUST STAY IN SYNC WITH `backend/routes/cara.py` → `ROLE_ALIASES`.
 * If you add a role on either side, add it on both.
 */
export const CARA_ROLE_FROM_USER = {
  // Family / patient side
  patient: 'family',
  family:  'family',

  // Clinician (assigned to a placement)
  clinician: 'clinician',

  // Receiving provider (post-acute agency ops) — canonical is `provider`;
  // legacy `agency`/`provider` variants all fold to the same tab.
  provider:               'provider',
  receiving_provider:     'provider',
  agency:                 'provider',
  care_provider_agency:   'provider',
  staffing_agency:        'provider',
  home_health_agency:     'provider',

  // Hospital / discharge-planner side
  planner:            'planner',
  case_manager:       'planner',
  discharge_planner:  'planner',
  hospital:           'planner',
  hospital_admin:     'planner',

  // Payer users hit the parked-workspace short-circuit — no tab.
  payer:        null,
  insurance:    null,
  health_plan:  null,

  // Platform admin
  admin:        'admin',
  super_admin:  'admin',
};

/**
 * Resolve a raw user role to the Cara tab key. Returns `null` for
 * unknown roles AND for parked roles (payer variants) so the caller
 * can render the "workspace not available" surface instead of the
 * chat tabs. Case-insensitive.
 */
export function resolveCaraRole(userRole) {
  if (!userRole) return null;
  const mapped = CARA_ROLE_FROM_USER[String(userRole).toLowerCase()];
  return mapped === undefined ? null : mapped;
}
